import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2,
  AlertTriangle,
  Calendar,
  TrendingUp,
  TrendingDown,
  Target,
  CheckSquare,
  Square,
  ArrowRight,
  Play,
  Circle,
  Trash2,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import { api } from '@/utils/api';
import toast from 'react-hot-toast';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { invalidatePlannerData } from '@/store/slices/plannerSlice';

interface ActionBlock {
  action: string;
  data: any;
  description?: string;
}

interface AIResponseRendererProps {
  content: string;
  isStreaming?: boolean;
  onSendMessage?: (message: string) => void;
}

// Normalize action block: ensure data wrapper exists
function normalizeActionBlock(parsed: any): ActionBlock | null {
  if (!parsed.action || parsed.action === 'create_track') return null;

  // If 'data' field exists, use it directly
  if (parsed.data) {
    return { action: parsed.action, data: parsed.data, description: parsed.description };
  }

  // Otherwise, extract all non-meta fields as data
  const { action, description, ...data } = parsed;
  return { action, data, description };
}

// Parse action blocks from AI response
function parseActionBlocks(content: string): { text: string; actions: ActionBlock[] } {
  const actions: ActionBlock[] = [];
  let text = content;

  // 1. Find ```json:action blocks (preferred format — triple backticks)
  const actionRegex = /```json:action\s*([\s\S]*?)```/g;
  let match;
  while ((match = actionRegex.exec(content)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      const normalized = normalizeActionBlock(parsed);
      if (normalized) actions.push(normalized);
    } catch {}
  }

  // 2. Fallback: find ```json blocks that contain an "action" field
  if (actions.length === 0) {
    const jsonRegex = /```json\s*([\s\S]*?)```/g;
    while ((match = jsonRegex.exec(content)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        if (parsed.action) {
          const normalized = normalizeActionBlock(parsed);
          if (normalized) actions.push(normalized);
        }
      } catch {}
    }
  }

  // 3. Fallback: LLM sometimes uses single backticks (`json:action...`) instead of triple
  if (actions.length === 0) {
    const singleTickRegex = /`json:action\s*([\s\S]*?)`/g;
    while ((match = singleTickRegex.exec(content)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        const normalized = normalizeActionBlock(parsed);
        if (normalized) actions.push(normalized);
      } catch {}
    }
  }

  // 4. Fallback: detect bare JSON objects with "action" field (no code fences at all)
  if (actions.length === 0) {
    const bareJsonRegex = /(\{"action"\s*:\s*"[^"]+?"[\s\S]*?\})\s*(?:\n|$)/g;
    while ((match = bareJsonRegex.exec(content)) !== null) {
      try {
        const parsed = JSON.parse(match[1].trim());
        if (parsed.action) {
          const normalized = normalizeActionBlock(parsed);
          if (normalized) actions.push(normalized);
        }
      } catch {}
    }
  }

  // Remove action blocks from display text (all formats)
  text = text.replace(/```json:action[\s\S]*?```/g, '').trim();
  text = text.replace(/```json[\s\S]*?"action"\s*:[\s\S]*?```/g, '').trim();
  text = text.replace(/`json:action[\s\S]*?`/g, '').trim();
  // Remove bare JSON action objects (lines starting with {"action":)
  text = text.replace(/\{"action"\s*:\s*"[^"]+?"[\s\S]*?\}\s*(?:\n|$)/g, '').trim();

  return { text, actions };
}

// Render markdown-like text with basic formatting
function RichText({ content, onSendMessage }: { content: string; onSendMessage?: (msg: string) => void }) {
  if (!content) return null;

  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  let orderedItems: string[] = [];

  // Helper: render text with choice support
  const renderText = (text: string) => {
    if (/\{\{choice:.*?\}\}/.test(text)) {
      return formatChoiceText(text, onSendMessage);
    }
    return formatInlineText(text);
  };

  const flushList = () => {
    if (listItems.length > 0) {
      // If ALL items are choice markers, render as a button group instead of a bullet list
      const allChoices = listItems.every(item => /^\{\{choice:.*?\}\}$/.test(item.trim()));
      if (allChoices && onSendMessage) {
        const choices = listItems.map(item => {
          const m = item.trim().match(/^\{\{choice:(.*?)\}\}$/);
          return m ? m[1] : item;
        });
        elements.push(
          <div key={`choices-${elements.length}`} className="flex flex-wrap gap-2 my-2">
            {choices.map((label, i) => (
              <button
                key={i}
                onClick={() => onSendMessage(label)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-800/50 active:scale-95 transition-all cursor-pointer border border-violet-200 dark:border-violet-700/50"
              >
                {label}
              </button>
            ))}
          </div>
        );
      } else {
        elements.push(
          <ul key={`list-${elements.length}`} className="space-y-1 my-2">
            {listItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                <span>{renderText(item)}</span>
              </li>
            ))}
          </ul>
        );
      }
      listItems = [];
    }
  };

  const flushOrderedList = () => {
    if (orderedItems.length > 0) {
      elements.push(
        <ol key={`olist-${elements.length}`} className="space-y-1 my-2">
          {orderedItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span className="mt-0.5 text-xs font-semibold text-gray-500 min-w-[20px]">{i + 1}.</span>
              <span>{renderText(item)}</span>
            </li>
          ))}
        </ol>
      );
      orderedItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      flushOrderedList();
      continue;
    }

    // Headers
    if (trimmed.startsWith('### ')) {
      flushList(); flushOrderedList();
      elements.push(
        <h4 key={`h4-${i}`} className="text-sm font-bold text-gray-900 dark:text-white mt-3 mb-1">
          {formatInlineText(trimmed.slice(4))}
        </h4>
      );
      continue;
    }
    if (trimmed.startsWith('## ')) {
      flushList(); flushOrderedList();
      elements.push(
        <h3 key={`h3-${i}`} className="text-sm font-bold text-gray-900 dark:text-white mt-3 mb-1">
          {formatInlineText(trimmed.slice(3))}
        </h3>
      );
      continue;
    }

    // Bullet list
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ') || trimmed.startsWith('* ')) {
      flushOrderedList();
      listItems.push(trimmed.slice(2));
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(trimmed)) {
      flushList();
      orderedItems.push(trimmed.replace(/^\d+\.\s/, ''));
      continue;
    }

    // Status line (emoji indicators)
    if (/^[✅⬜⚠️🔥📊📚📅❌]/.test(trimmed)) {
      flushList(); flushOrderedList();
      elements.push(
        <div key={`status-${i}`} className="flex items-start gap-2 text-sm py-0.5">
          <span>{formatInlineText(trimmed)}</span>
        </div>
      );
      continue;
    }

    // Metric line (contains numbers with context)
    if (/^\s*(Today|This week|This month|Current|Completion|Time|Average|Total):/.test(trimmed)) {
      flushList(); flushOrderedList();
      const [label, ...valueParts] = trimmed.split(':');
      const value = valueParts.join(':').trim();
      elements.push(
        <MetricLine key={`metric-${i}`} label={label.trim()} value={value} />
      );
      continue;
    }

    // Regular paragraph (with optional choice buttons)
    flushList(); flushOrderedList();
    const hasChoices = /\{\{choice:.*?\}\}/.test(trimmed);
    elements.push(
      <p key={`p-${i}`} className="text-sm text-gray-800 dark:text-gray-200 my-1">
        {hasChoices ? formatChoiceText(trimmed, onSendMessage) : formatInlineText(trimmed)}
      </p>
    );
  }

  flushList();
  flushOrderedList();

  return <>{elements}</>;
}

// Format text containing {{choice:label}} markers as inline clickable buttons
function formatChoiceText(text: string, onSendMessage?: (msg: string) => void): JSX.Element {
  const parts: (string | JSX.Element)[] = [];
  const choiceRegex = /\{\{choice:(.*?)\}\}/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = choiceRegex.exec(text)) !== null) {
    // Add text before this choice (with inline formatting)
    if (match.index > lastIndex) {
      const segment = text.slice(lastIndex, match.index);
      parts.push(<span key={`t-${key++}`}>{formatInlineText(segment)}</span>);
    }

    const label = match[1];
    parts.push(
      <button
        key={`choice-${key++}`}
        onClick={() => onSendMessage?.(label)}
        className="inline-flex items-center px-3 py-1 mx-0.5 rounded-full text-xs font-semibold bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-800/50 active:scale-95 transition-all cursor-pointer border border-violet-200 dark:border-violet-700/50 align-middle"
      >
        {label}
      </button>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const segment = text.slice(lastIndex);
    parts.push(<span key={`t-${key++}`}>{formatInlineText(segment)}</span>);
  }

  return <>{parts}</>;
}

// Format inline text (bold, italic, code)
function formatInlineText(text: string): JSX.Element | string {
  // Simple bold/italic/code parsing
  const parts: (string | JSX.Element)[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
    // Code: `text`
    const codeMatch = remaining.match(/`(.*?)`/);

    const matches = [
      boldMatch ? { index: boldMatch.index!, length: boldMatch[0].length, type: 'bold', content: boldMatch[1] } : null,
      codeMatch ? { index: codeMatch.index!, length: codeMatch[0].length, type: 'code', content: codeMatch[1] } : null,
    ].filter(Boolean).sort((a, b) => a!.index - b!.index);

    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = matches[0]!;
    if (first.index > 0) {
      parts.push(remaining.slice(0, first.index));
    }

    if (first.type === 'bold') {
      parts.push(<strong key={key++} className="font-semibold text-gray-900 dark:text-white">{first.content}</strong>);
    } else if (first.type === 'code') {
      parts.push(
        <code key={key++} className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono">
          {first.content}
        </code>
      );
    }

    remaining = remaining.slice(first.index + first.length);
  }

  return <>{parts}</>;
}

// Metric display
function MetricLine({ label, value }: { label: string; value: string }) {
  // Detect trends
  const hasUp = /up|increase|improve|\+\d/i.test(value);
  const hasDown = /down|decrease|drop|-\d/i.test(value);

  // Extract percentage if present
  const percentMatch = value.match(/(\d+)%/);

  return (
    <div className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 my-1">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-white">{value}</span>
        {hasUp && <TrendingUp className="w-3.5 h-3.5 text-green-500" />}
        {hasDown && <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
        {percentMatch && !hasUp && !hasDown && (
          <div className="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-violet-500"
              style={{ width: `${Math.min(100, parseInt(percentMatch[1]))}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Action preview card
function ActionPreviewCard({ action, onExecute, onDismiss }: {
  action: ActionBlock;
  onExecute: () => void;
  onDismiss: () => void;
}) {
  const [isExecuting, setIsExecuting] = useState(false);

  const handleExecute = async () => {
    setIsExecuting(true);
    await onExecute();
    setIsExecuting(false);
  };

  const getActionIcon = () => {
    switch (action.action) {
      case 'mark_complete': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'complete_today': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'incomplete_today': return <Circle className="w-4 h-4 text-orange-500" />;
      case 'delete_today': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'delete_item': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'bulk_delete': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'bulk_complete': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'reschedule': return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'create_task': return <Target className="w-4 h-4 text-violet-500" />;
      case 'create_task_group': return <Target className="w-4 h-4 text-violet-500" />;
      case 'schedule_cards': return <Calendar className="w-4 h-4 text-violet-500" />;
      case 'bulk_reschedule': return <ArrowRight className="w-4 h-4 text-orange-500" />;
      case 'update_priority': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'update_difficulty': return <Target className="w-4 h-4 text-blue-500" />;
      default: return <Play className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = () => {
    switch (action.action) {
      case 'mark_complete': return 'Mark Complete';
      case 'complete_today': {
        const cDate = action.data?.date;
        return cDate ? `Complete All on ${cDate}` : 'Complete All Today';
      }
      case 'incomplete_today': {
        const iDate = action.data?.date;
        return iDate ? `Mark Incomplete on ${iDate}` : 'Mark All Incomplete';
      }
      case 'delete_today': {
        const dDate = action.data?.date;
        return dDate ? `Delete All on ${dDate}` : 'Delete All Today';
      }
      case 'delete_item': return `Delete "${action.data?.title || 'Item'}"`;
      case 'bulk_delete': return `Delete ${action.data?.entityIds?.length || 0} Items`;
      case 'bulk_complete': return 'Complete All';
      case 'reschedule': return 'Reschedule';
      case 'create_task': return 'Create Task';
      case 'create_task_group': return `Create "${action.data?.title || 'Task'}" with ${action.data?.subtasks?.length || 0} Subtasks`;
      case 'schedule_cards': return `Schedule ${action.data?.tasks?.length || 0} Tasks`;
      case 'bulk_reschedule': return 'Reschedule All';
      case 'update_priority': return 'Update Priority';
      case 'update_difficulty': return `Update Difficulty${action.data?.difficulty ? ` to ${action.data.difficulty}` : ''}`;
      default: return 'Execute';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
    >
      <div className="flex items-start gap-2 mb-2">
        {getActionIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {getActionLabel()}
          </p>
          {action.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {action.description}
            </p>
          )}
        </div>
      </div>

      {/* Show details for schedule_cards */}
      {action.action === 'schedule_cards' && action.data?.tasks && (
        <div className="mb-2 space-y-1">
          {action.data.tasks.slice(0, 5).map((task: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <Square className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{task.title}</span>
              {task.date && <span className="text-gray-400 flex-shrink-0">{task.date}</span>}
            </div>
          ))}
          {action.data.tasks.length > 5 && (
            <p className="text-xs text-gray-400 ml-5">
              +{action.data.tasks.length - 5} more
            </p>
          )}
        </div>
      )}

      {/* Show details for create_task_group */}
      {action.action === 'create_task_group' && action.data?.subtasks && (
        <div className="mb-2 space-y-1">
          {action.data.subtasks.slice(0, 8).map((subtask: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <Square className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{typeof subtask === 'string' ? subtask : subtask?.title || subtask}</span>
            </div>
          ))}
          {action.data.subtasks.length > 8 && (
            <p className="text-xs text-gray-400 ml-5">
              +{action.data.subtasks.length - 8} more
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleExecute}
          disabled={isExecuting}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all',
            'bg-violet-600 hover:bg-violet-700 text-white',
            'disabled:opacity-50'
          )}
        >
          {isExecuting ? (
            <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <CheckSquare className="w-3 h-3" />
          )}
          Confirm
        </button>
        <button
          onClick={onDismiss}
          className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          Dismiss
        </button>
      </div>
    </motion.div>
  );
}

// Actions that affect the planner and require a data refresh
const PLANNER_AFFECTING_ACTIONS = [
  'bulk_reschedule', 'reschedule', 'create_task', 'schedule_cards',
  'mark_complete', 'delete_item', 'bulk_delete', 'set_due_date', 'complete_today', 'create_task_group',
  'incomplete_today', 'delete_today', 'bulk_complete', 'complete_habit',
];

// Main component
export function AIResponseRenderer({ content, isStreaming, onSendMessage }: AIResponseRendererProps) {
  const [dismissedActions, setDismissedActions] = useState<Set<number>>(new Set());
  const dispatch = useAppDispatch();

  if (!content) return null;

  const { text, actions } = parseActionBlocks(content);

  // If there's nothing to render specially, just return null (caller handles plain text)
  if (!text && actions.length === 0) return null;

  const handleExecuteAction = async (action: ActionBlock, index: number) => {
    try {
      const response = await api.post('/ai/execute-action', {
        action: action.action,
        data: action.data,
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Action completed');
        setDismissedActions(prev => new Set(prev).add(index));

        // Invalidate planner data so the Planner page refetches
        if (PLANNER_AFFECTING_ACTIONS.includes(action.action)) {
          dispatch(invalidatePlannerData());
        }
      } else {
        toast.error(response.data.message || 'Action failed');
      }
    } catch (error) {
      toast.error('Failed to execute action');
    }
  };

  const handleDismissAction = (index: number) => {
    setDismissedActions(prev => new Set(prev).add(index));
  };

  return (
    <div className="space-y-1">
      {/* Rich text content */}
      <RichText content={text} onSendMessage={onSendMessage} />

      {/* Streaming cursor */}
      {isStreaming && (
        <span className="inline-block w-2 h-4 ml-1 bg-gray-500 animate-pulse" />
      )}

      {/* Action previews */}
      {!isStreaming && actions.length > 0 && (
        <AnimatePresence>
          {actions.map((action, i) =>
            !dismissedActions.has(i) ? (
              <ActionPreviewCard
                key={i}
                action={action}
                onExecute={() => handleExecuteAction(action, i)}
                onDismiss={() => handleDismissAction(i)}
              />
            ) : (
              <motion.div
                key={`done-${i}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 mt-2 text-xs text-green-600 dark:text-green-400"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Action completed
              </motion.div>
            )
          )}
        </AnimatePresence>
      )}
    </div>
  );
}

export default AIResponseRenderer;
